import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ItemSheetData } from "@/types/itemsheet";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  SortingState,
  getSortedRowModel,
} from "@tanstack/react-table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { useMemo, useState, useEffect } from "react";
import { ReactComponent as FolderMoveSvg } from "@/assets/images/folder-move-outline.svg";
import { useIsFinancialManager } from "@/hooks/useIsFinancialManager";
import EstimateBadge from "@/pages/Project/Estimate/components/EstimateBadge";
import { ItemSheetExtraTag } from "@/consts/Estimate";
import { ChevronUp, ChevronDown } from "lucide-react";

interface Props {
  items: ItemSheetData[];
  onMoveItem: (item: ItemSheetData, toBuyer: string) => void;
  onCostChange: (item: ItemSheetData, newCost: number) => void;
  divisionOptions: string[]; // 이동 가능한 디비전 목록
  discountRate?: number;
}

const EditableCostCell = ({
  value,
  onChange,
}: {
  value: number;
  onChange: (newCost: number) => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(String(value || 0));

  useEffect(() => {
    setLocalValue(String(value || 0));
  }, [value]);

  const handleBlur = () => {
    const parsed = parseFloat(localValue);
    if (!isNaN(parsed) && parsed >= 0) {
      onChange(parsed);
    } else {
      setLocalValue(String(value || 0));
    }
    setIsEditing(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleBlur();
    }
  };

  const handleClick = () => {
    setIsEditing(true);
  };

  if (isEditing) {
    return (
      <input
        type="number"
        min={0}
        value={localValue}
        className="block w-full px-1 py-[2px] border-none text-left text-xs bg-transparent"
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        onKeyPress={handleKeyPress}
        autoFocus
      />
    );
  }

  return (
    <span
      className="block w-full px-1 py-[2px] text-left text-xs cursor-pointer hover:bg-gray-100"
      onClick={handleClick}
    >
      {(value || 0).toLocaleString()}
    </span>
  );
};

export default function InnerProductTable({
  items,
  onMoveItem,
  onCostChange,
  divisionOptions,
  discountRate = 0,
}: Props) {
  const [openPopoverUuid, setOpenPopoverUuid] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);

  const isFinancialManager = useIsFinancialManager();

  const filteredDivisions = useMemo(() => {
    return (item: ItemSheetData) => {
      if (!divisionOptions || !Array.isArray(divisionOptions)) {
        return [];
      }
      return divisionOptions.filter((buyer) => buyer !== item.buyer);
    };
  }, [divisionOptions]);

  const columns: ColumnDef<ItemSheetData>[] = [
    {
      accessorKey: "product_name",
      header: () => <span className="text-sm font-semibold">제품명</span>,
      cell: ({ row }) => {
        return (
          <span className="text-xs text-right">
            {row.original.product_name}
            {row.original.isCustomPrice && (
              <EstimateBadge type={ItemSheetExtraTag.CUSTOM_PRICE} noLabel />
            )}
            {row.original.isFloorPrice && (
              <EstimateBadge type={ItemSheetExtraTag.FLOOR_PRICE} noLabel />
            )}
          </span>
        );
      },
      size: 144,
    },
    {
      accessorKey: "product_id",
      header: () => <span className="text-sm font-semibold">제품번호</span>,
      size: 115,
    },
    {
      id: "total_list_price",
      header: () => <span className="text-sm font-semibold">판매가</span>,
      cell: ({ row }) => {
        const quantity = row.original.quantity ?? 0;
        const listPrice = row.original.list_price ?? 0;
        const totalListPrice = quantity * listPrice;

        return (
          <span className="text-xs text-right">
            {totalListPrice.toLocaleString()}
          </span>
        );
      },
      size: 115,
    },
    {
      id: "supply_price",
      header: () => <span className="text-sm font-semibold">제안가격</span>,
      cell: ({ row }) => {
        const quantity = row.original.quantity ?? 0;
        const listPrice = row.original.list_price ?? 0;
        const totalListPrice = quantity * listPrice;
        const supplyPrice = totalListPrice * (1 - discountRate / 100);

        return (
          <span className="text-xs text-right">
            {supplyPrice.toLocaleString()}
          </span>
        );
      },
      size: 115,
    },
    ...(isFinancialManager
      ? [
          {
            id: "total_cost",
            header: () => (
              <span className="text-sm font-semibold">매입가/원가</span>
            ),
            cell: ({ row }: { row: any }) => (
              <div className="text-xs text-left">
                <EditableCostCell
                  value={row.original.total_cost || 0}
                  onChange={(newCost) => onCostChange(row.original, newCost)}
                />
              </div>
            ),
            size: 115,
          },
          {
            id: "margin",
            header: () => (
              <span className="text-sm font-semibold">마진금액</span>
            ),
            cell: ({ row }: { row: any }) => {
              const quantity = row.original.quantity ?? 0;
              const listPrice = row.original.list_price ?? 0;
              const totalListPrice = quantity * listPrice;
              const supplyPrice = totalListPrice * (1 - discountRate / 100);

              const unitCost = row.original.unit_cost ?? 0;
              const totalCost = row.original.total_cost ?? unitCost * quantity;

              const margin = supplyPrice - totalCost;

              return (
                <span className="text-xs text-right">
                  {margin.toLocaleString()}
                </span>
              );
            },
            size: 115,
          },
        ]
      : []),
    {
      id: "marginRate",
      header: ({ column }: { column: any }) => (
        <div className="flex items-center gap-1">
          <span className="text-sm font-semibold">마진율</span>
          <button
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="flex items-center hover:text-gray-300"
          >
            {column.getIsSorted() === "asc" ? (
              <ChevronUp className="w-4 h-4" />
            ) : column.getIsSorted() === "desc" ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <div className="flex flex-col">
                <ChevronUp className="w-3 h-3 -mb-1" />
                <ChevronDown className="w-3 h-3" />
              </div>
            )}
          </button>
        </div>
      ),
      accessorFn: (row: ItemSheetData) => {
        const quantity = row.quantity ?? 0;
        const listPrice = row.list_price ?? 0;
        const totalListPrice = quantity * listPrice;
        const supplyPrice = totalListPrice * (1 - discountRate / 100);

        const unitCost = row.unit_cost ?? 0;
        const totalCost = row.total_cost ?? unitCost * quantity;

        const margin = supplyPrice - totalCost;
        const marginRate = supplyPrice > 0 ? (margin / supplyPrice) * 100 : 0;

        return marginRate;
      },
      cell: ({ row }) => {
        const quantity = row.original.quantity ?? 0;
        const listPrice = row.original.list_price ?? 0;
        const totalListPrice = quantity * listPrice;
        const supplyPrice = totalListPrice * (1 - discountRate / 100);

        const unitCost = row.original.unit_cost ?? 0;
        const totalCost = row.original.total_cost ?? unitCost * quantity;

        const margin = supplyPrice - totalCost;
        const marginRate = supplyPrice > 0 ? (margin / supplyPrice) * 100 : 0;

        return <span className="text-xs">{marginRate.toFixed(1)}%</span>;
      },
      size: 115,
    },
    {
      id: "division",
      header: () => <span className="text-sm font-semibold">디비전</span>,
      cell: ({ row }) => {
        const originalBuyer = (row.original as any).originalBuyer;
        let displayBuyer;
        if (originalBuyer === undefined) {
          // originalBuyer가 undefined인 경우 현재 buyer 사용 (이동 안한상태)
          displayBuyer = row.original.buyer || "TPP";
        } else if (originalBuyer === null || originalBuyer.trim() === "") {
          // originalBuyer가 명시적으로 null이거나 빈 문자열인 경우 TPP
          displayBuyer = "TPP";
        } else {
          displayBuyer = originalBuyer;
        }

        return (
          <span className="text-xs text-gray-A font-normal">
            {displayBuyer}
          </span>
        );
      },
      size: 100,
    },
    {
      id: "actions",
      header: () => <span className="text-sm font-semibold"></span>,
      cell: ({ row }) => {
        const item = row.original;
        const availableTargets = filteredDivisions(item);

        return (
          <Popover
            open={openPopoverUuid === item.uuid}
            onOpenChange={(open) =>
              setOpenPopoverUuid(open ? (item.uuid ?? null) : null)
            }
          >
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="h-4 w-4">
                <FolderMoveSvg className="w-4 h-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto min-w-24 p-0 max-h-60 overflow-y-auto z-50">
              {availableTargets && availableTargets.length > 0 ? (
                availableTargets.map((buyer) => (
                  <button
                    key={buyer}
                    className="w-full px-5 py-2 text-left text-gray-A text-xs font-normal hover:bg-gray-E block"
                    onClick={() => {
                      onMoveItem(item, buyer);
                      setOpenPopoverUuid(null);
                    }}
                  >
                    {buyer}
                  </button>
                ))
              ) : (
                <div className="px-5 py-2 text-xs text-gray-C">
                  이동 가능한 디비전이 없습니다.
                </div>
              )}
            </PopoverContent>
          </Popover>
        );
      },
      size: 40,
    },
  ];

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    initialState: {
      pagination: {
        pageSize: Number.MAX_SAFE_INTEGER,
      },
    },
  });

  const totalColumnWidth = columns.reduce(
    (sum, col) => sum + (col.size ?? 0),
    0
  );

  return (
    <div className="bg-white overflow-x-auto">
      <Table
        style={{
          tableLayout: "fixed",
          minWidth: `${totalColumnWidth}px`,
        }}
      >
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    className="px-2 py-[10px] text-sm text-white bg-gray-A whitespace-nowrap"
                    style={{
                      minWidth: header.column.columnDef.size,
                      maxWidth: header.column.columnDef.size,
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                data-state={row.getIsSelected() && "selected"}
                key={row.id}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    style={{
                      minWidth: cell.column.columnDef.size,
                      maxWidth: cell.column.columnDef.size,
                    }}
                    className={`px-2 py-[10px] ${
                      cell.column.id === "product_name"
                        ? ""
                        : "whitespace-nowrap"
                    }`}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                데이터가 없습니다.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
